// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Main-thread client for the zip worker. Owns the single Worker instance and
 * exposes promise-based RPC with progress callbacks. Each call gets a unique id
 * so concurrent operations don't cross wires.
 */

import type {
  ExtractedFile,
  InputFile,
  WorkerRequest,
  WorkerResponse,
  ZipEntry,
} from './types';

export type ProgressFn = (done: number, total: number) => void;

/** Distributive Omit so the discriminated union keeps each variant's keys. */
type RequestBody = WorkerRequest extends infer T
  ? T extends { id: number }
    ? Omit<T, 'id'>
    : never
  : never;

let worker: Worker | null = null;
let nextId = 1;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  }
  return worker;
}

/** Tear down the worker (e.g. on a fatal error) so the next call gets a fresh one. */
export function resetWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
}

function call<T>(
  request: RequestBody,
  onProgress: ProgressFn | undefined,
  resolve: (res: WorkerResponse) => T | null,
  transfer: Transferable[] = [],
): Promise<T> {
  const w = getWorker();
  const id = nextId++;
  return new Promise<T>((res, rej) => {
    const handler = (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (msg.id !== id) return;
      if (msg.type === 'progress') {
        onProgress?.(msg.done, msg.total);
        return;
      }
      w.removeEventListener('message', handler);
      if (msg.type === 'error') {
        rej(new Error(msg.message));
        return;
      }
      const out = resolve(msg);
      if (out === null) {
        rej(new Error('Unexpected worker response'));
      } else {
        res(out);
      }
    };
    w.addEventListener('message', handler);
    w.postMessage({ ...request, id } as WorkerRequest, transfer);
  });
}

/** List the entries of a ZIP without decompressing. Consumes `buffer`. */
export function listZip(buffer: ArrayBuffer): Promise<ZipEntry[]> {
  return call<ZipEntry[]>(
    { kind: 'list', buffer },
    undefined,
    (msg) => (msg.type === 'listed' ? msg.entries : null),
    [buffer],
  );
}

/** Extract files from a ZIP. `paths` selects entries; omit for all. */
export function extractZip(
  buffer: ArrayBuffer,
  paths: string[] | undefined,
  onProgress?: ProgressFn,
): Promise<ExtractedFile[]> {
  return call<ExtractedFile[]>(
    { kind: 'extract', buffer, paths },
    onProgress,
    (msg) => (msg.type === 'extracted' ? msg.files : null),
    [buffer],
  );
}

/** Build a ZIP from input files. */
export function createZip(
  files: InputFile[],
  level: number,
  onProgress?: ProgressFn,
): Promise<Uint8Array> {
  const transfer = files.map((f) => f.data.buffer);
  return call<Uint8Array>(
    { kind: 'create', files, level },
    onProgress,
    (msg) => (msg.type === 'created' ? msg.data : null),
    transfer,
  );
}
